import { Link } from 'react-router-dom';
import { ArrowRight, Check, ClipboardCheck, Compass, Hammer, Linkedin, Medal, Quote, X } from 'lucide-react';

export const ABOUT_IMG = 'https://images.unsplash.com/photo-1758270705518-b61b40527e76?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const FOUNDER_IMG = 'https://customer-assets.emergentagent.com/job_d947f440-07f8-41e8-916c-6e89d8965b4e/artifacts/wn30i42g_IMG_9375%20copy.jpg%20%281%29.jpeg';

const Eyebrow = ({ children, light }) => <p className={`text-[11px] font-semibold tracking-[0.25em] uppercase mb-4 ${light ? 'text-teal-300' : 'text-teal-700'}`}>{children}</p>;

export const AboutHero = () => (
  <section className="relative bg-[#0b1f4d] text-white overflow-hidden" data-testid="about-hero">
    <img src={ABOUT_IMG} alt="Students working together on a laptop" className="absolute inset-0 w-full h-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f4d] via-[#0b1f4d]/90 to-[#0b1f4d]/40" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 lg:pt-40 lg:pb-32">
      <Eyebrow light>About Elevate Careers</Eyebrow>
      <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl animate-in fade-in slide-in-from-bottom-3 duration-700">
        Talent is everywhere in India. <span className="text-teal-300">Career readiness isn't.</span>
      </h1>
      <p className="mt-7 text-lg text-slate-300 max-w-2xl leading-relaxed">We exist to close the gap between a degree and a first real job, especially for students outside the metros who have the ability but not the network, guidance or proof that employers look for.</p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/network" data-testid="about-hero-cta" className="group inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full transition-colors">Start your journey free <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></Link>
        <Link to="/faq" data-testid="about-hero-faq" className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors">Read the FAQ</Link>
      </div>
    </div>
  </section>
);

const GAP = [
  ['~1 in 2', 'Indian graduates are assessed as job-ready', 'India Skills Report'],
  ['Proof > degree', 'Recruiters shortlist on projects, practical skills and how you communicate', 'What hiring managers tell us'],
  ['Tier-2 & 3', 'Students often have the talent but little access to mentors or industry insight', 'Our focus'],
];

export const AboutGap = () => (
  <section className="bg-white" data-testid="about-gap">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
      <div>
        <Eyebrow>The problem we solve</Eyebrow>
        <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-[#0b1f4d] leading-tight">More courses aren't the answer. Direction and proof are.</h2>
        <p className="mt-5 text-slate-600 leading-relaxed">Most students don't fail because they lack effort. They pick a path by guesswork, collect certificates no recruiter checks, and apply everywhere without a plan. We fix the order: understand the gap first, then build exactly what employers screen for.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {GAP.map(([v, t, s], i) => (
          <div key={v} style={{ animationDelay: `${i * 120}ms` }} className="rounded-3xl border border-slate-200 p-6 animate-in fade-in slide-in-from-bottom-2 duration-700 fill-mode-both" data-testid={`about-gap-${i}`}>
            <p className="font-outfit text-3xl font-bold text-[#0b1f4d]">{v}</p>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{t}</p>
            <p className="text-[11px] uppercase tracking-widest text-teal-700 mt-4">{s}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const METHOD = [
  [Compass, 'Diagnose', 'A free Career Readiness Check and a 45-minute one-to-one consultation show where you really stand.'],
  [ClipboardCheck, 'Plan', 'You get target roles and a personalised career action plan, reviewed and approved by a consultant.'],
  [Hammer, 'Practise', 'Real work tasks for your role, built from what employers check, each reviewed by a person.'],
  [Medal, 'Prove', 'A portfolio project, a sharper resume and LinkedIn, interview practice and weekly human check-ins.'],
];

export const AboutMethod = () => (
  <section className="bg-[#0b1f4d] text-white" data-testid="about-method">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <Eyebrow light>The Elevate method</Eyebrow>
      <h2 className="font-outfit font-bold text-3xl sm:text-4xl max-w-2xl leading-tight">Four steps from "I'm not sure" to "I can show you."</h2>
      <ol className="mt-14 grid md:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden">
        {METHOD.map(([Icon, t, d], i) => (
          <li key={t} className="bg-[#0b1f4d] p-7 hover:bg-[#10275e] transition-colors" data-testid={`about-method-${t.toLowerCase()}`}>
            <p className="font-outfit text-sm text-teal-300">0{i + 1}</p>
            <Icon className="w-7 h-7 text-teal-300 mt-6" />
            <p className="font-outfit text-xl font-semibold mt-4">{t}</p>
            <p className="text-sm text-slate-300 mt-2 leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const COMPARE = [
  ['Starts with a diagnosis of your gaps', false],
  ['A real person reviews your work', false],
  ['Tasks built from what employers screen for', false],
  ['Separate tracks for freshers and professionals', false],
  ['Live openings matched to your target roles', false],
  ['Honest advice, no placement guarantees', false],
];

export const AboutDifference = () => (
  <section className="bg-slate-50" data-testid="about-difference">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <Eyebrow>Why we're different</Eyebrow>
      <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-[#0b1f4d] leading-tight max-w-2xl">Not another course library.</h2>
      <div className="mt-10 rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[1fr_110px_110px] sm:grid-cols-[1fr_160px_160px] text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
          <p className="p-4">What you get</p><p className="p-4 text-center">Typical course</p><p className="p-4 text-center bg-[#0b1f4d] text-teal-300">Elevate</p>
        </div>
        {COMPARE.map(([t]) => (
          <div key={t} className="grid grid-cols-[1fr_110px_110px] sm:grid-cols-[1fr_160px_160px] border-b border-slate-100 last:border-0 text-sm" data-testid="about-compare-row">
            <p className="p-4 text-[#0b1f4d]">{t}</p>
            <p className="p-4 flex justify-center"><X className="w-4 h-4 text-slate-300" /></p>
            <p className="p-4 flex justify-center bg-teal-50/60"><Check className="w-4 h-4 text-teal-600" /></p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PROMISES = [['We will', ['Tell you honestly where you stand', 'Recommend only what fits you', 'Review your work as a person, not a bot', 'Keep your DIY learning available long-term']],
  ['We won\'t', ['Guarantee a job or placement', 'Push programs you don\'t need', 'Sell certificates as a shortcut', 'Share your data with employers without consent']]];

export const AboutPromise = () => (
  <section className="bg-white" data-testid="about-promise">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid md:grid-cols-2 gap-6">
      {PROMISES.map(([t, items], i) => (
        <div key={t} className={`rounded-3xl p-8 ${i ? 'bg-slate-50 border border-slate-200' : 'bg-teal-50 border border-teal-100'}`}>
          <p className="font-outfit text-2xl font-bold text-[#0b1f4d]">{t}</p>
          <ul className="mt-6 space-y-3">{items.map((x) => <li key={x} className="flex gap-3 text-slate-700">{i ? <X className="w-5 h-5 text-slate-400 shrink-0" /> : <Check className="w-5 h-5 text-teal-600 shrink-0" />}{x}</li>)}</ul>
        </div>
      ))}
    </div>
  </section>
);

export const AboutFounder = () => (
  <section className="bg-slate-50" data-testid="about-founder">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid md:grid-cols-[320px_1fr] gap-12 items-center">
      <div className="relative">
        <div className="absolute -inset-3 rounded-[2rem] bg-teal-400/20 rotate-3" />
        <img src={FOUNDER_IMG} alt="Shekhar Sati, Founder of Elevate Careers" className="relative w-full aspect-[4/5] object-cover object-top rounded-[1.75rem] shadow-xl" />
      </div>
      <div>
        <Eyebrow>The founder</Eyebrow>
        <Quote className="w-10 h-10 text-teal-400" />
        <p className="font-outfit text-2xl sm:text-3xl text-[#0b1f4d] leading-snug mt-4">I've sat on the hiring side for over a decade. The students who struggle rarely lack ability. They lack someone who shows them what good looks like.</p>
        <p className="mt-6 font-semibold text-[#0b1f4d]">Shekhar Sati</p>
        <p className="text-sm text-slate-500">Founder, Elevate Careers · 11+ years in technology and program leadership, interviewing and assessing professionals</p>
        <a href="https://www.linkedin.com/in/shekhar-sati-7717323aa/" target="_blank" rel="noopener noreferrer" data-testid="about-founder-linkedin"
          className="inline-flex items-center gap-2 mt-6 text-teal-700 font-semibold hover:text-teal-800"><Linkedin className="w-4 h-4" /> Connect on LinkedIn</a>
      </div>
    </div>
  </section>
);

export const AboutCta = () => (
  <section className="bg-[#0b1f4d] text-white" data-testid="about-cta">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
      <h2 className="font-outfit font-bold text-3xl sm:text-4xl">Your next step takes three minutes.</h2>
      <p className="mt-4 text-slate-300">Join the Talent Network free and take the Career Readiness Check. Consultations are one-to-one, so weekly slots are limited.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <Link to="/network" data-testid="about-cta-join" className="bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full transition-colors">Join free</Link>
        <Link to="/apply?plan=clarity-session" data-testid="about-cta-consult" className="border border-white/25 hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors">Book a ₹499 consultation</Link>
      </div>
    </div>
  </section>
);
